import {
  lazy,
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom-v5-compat";
import { BASE_PATH_DISRUPTION } from "@b2bportal/core-utilities";
import { logger, trackEvent } from "@hopper-b2b/api";
import { useTrackingProperties } from "@hopper-b2b/experiments";
import { useChatSupport, useKustomerChatSupport } from "@hopper-b2b/self-serve";
import { TripsList as UberTripsList } from "@hopper-b2b/trips/src/pages/UberTripsList";
import type {
  LaunchedApplicationProperties,
  RenderLodgingShopValuePropsVariant,
} from "@hopper-b2b/types";
import { EmptySlot, Spinner, type Slots } from "@hopper-b2b/ui";
import {
  type FeatureFlagsContextProps,
  getEnvVariables,
  getLandingScreen,
  PATH_FLIGHTS,
  PATH_HOTELS_ROOT,
  useLaunchedApplicationEvent,
} from "@hopper-b2b/utilities";
import type { Theme } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import clsx from "clsx";
import { UserContext } from "../../App";
import ChevronDown from "../../assets/client/chevron-down.svg?react";
import { useDarkModePreferred } from "../../utils/colors";
import config from "../../utils/config";
import { useNubankFeatureFlagsContext } from "../../utils/featureFlagsContext";
import { closeNubankWebview } from "../../utils/nubankHandoff";
import {
  getNupayOAuthRedirectLogMetadata,
  parseNupayOAuthCallbackSearch,
  resolveNupayOAuthCallbackRedirectUrl,
} from "../../utils/nupayOAuthCallbackRedirect";
import {
  PATH_ACCESS_RESTRICTED,
  PATH_FLIGHTS_SEARCH,
  PATH_FLIGHTS_SHOP,
  PATH_HOME,
  PATH_HOTELS_SEARCH,
  PATH_LANDING,
  PATH_OFFERS,
  PATH_TRIPS,
  ROUTE_FLIGHTS,
  ROUTE_HOTELS,
} from "../../utils/urlPaths";
import { AccessRestricted } from "../AccessRestricted";
import { BestPriceGuaranteeBanner } from "../BestPriceGuaranteeBanner";
import { ConfirmationScreen } from "../ConfirmationScreen";
import { ErrorScreen } from "../ErrorScreen";
import { FeatureProtectedRoute } from "../FeatureProtectedRoute";
import InputTextField from "../InputTextField";
import { Maintenance } from "../Maintenance";
import { SharedFlightCtaSection } from "../SharedFlightCtaSection";
import {
  AddTravelerFormSelect,
  ContactInfoForm,
  ContactSupportModalContent,
  ExchangeContactAirlineScreen,
  ExchangeLandingScreen,
  FareDetailsSliceHeader,
  FiltersModal,
  FilterTitle,
  FlightCardExpandedSection,
  FlightCheckoutCtaSection,
  FlightConfirmationTravelInsuranceIncluded,
  FlightNumberFilterSelection,
  FlightPriceDropProtectionBanner,
  FlightSearchMobileHeaderLeftContent,
  FlightShopLocationDestinationAutocomplete,
  FlightShopLocationOriginAutocomplete,
  FlightShopReviewInsuranceSection,
  FlightShopReviewMultiticketBanner,
  FlightShopSummaryPanel,
  FrequentFlyerDropdown,
  GalleryGrid,
  GalleryHeader,
  HotelCancellationConfirmationScreen,
  HotelCancellationSummaryPanel,
  HotelCheckoutCtaSection,
  HotelDetailsPolicies,
  HotelDetailsReview,
  HotelHeader,
  HotelListMapCard,
  HotelSearchAutocomplete,
  HotelShopMap,
  HotelVoucherOfferBanner,
  InputAdornmentLearnMore,
  InstallmentsBanner,
  Loader,
  LoadingScreen,
  LodgingCardCashBack,
  LodgingCardOriginalPrice,
  LodgingCardPrice,
  LodgingMapBackButton,
  LodgingSearch,
  LodgingSearchMobileGuestModal,
  MixedCabinHalfsheet,
  MobileActionLinks,
  MobileFlightPricePredictionContent,
  NoResults,
  NubankFlightCard,
  NubankFlightShopMobileHeader,
  PassengerSelectButton,
  PassportCountrySelect,
  PriceBreakdownAdditionalInfo,
  PriceWatchEntryCard,
  RadioDropdown,
  RequiredAssistanceSelect,
  RoomDetailsCta,
  RoomDetailsHeader,
  RoomDetailsModalHeader,
  RoomDetailsSelect,
  RoomGallery,
  SearchModal,
  SelfServeFlightInfo,
  ShopHeader,
  TravelerSelectRowButtonWrap,
  TripDetailsSummary,
  TripFlightsDetailsInsurance,
  TripsCard,
  TripsConfirmationCopyAndGoButton,
  TripsErrorModalContent,
  TripsFlightsDetailsBaggage,
  TripsFlightsDetailsSeats,
  TripsModalCloseIcon,
  OfferBanner,
  TripsFlightsPaymentDetailsAdditionalInfo,
  TripsFlightsConvenienceFee,
  BlackFridayBanner,
  TravellerFormModal,
  HeaderFormButtons,
} from "../Slots";
import FlightSearchRoute from "./FlightSearchRoute";
import HomeRouteComponent from "./HomeRouteComponent";
import LodgingSearchRoute from "./LodgingSearchRoute";
import OfferTakeoverSkeleton from "./OfferTakeoverSkeleton";
import useStyles from "./styles";
import "./styles.scss";

const FlightsRouteComponent = lazy(() => import("./FlightsRouteComponent"));
const LodgingRouteComponent = lazy(() => import("./LodgingRouteComponent"));
const TripRoute = lazy(() => import("./TripRouteComponent"));
const DisruptionExerciseRoute = lazy(() => import("./DisruptionExerciseRoute"));
const WatchesApp = lazy(() => import("./Watches"));
const OfferTakeoverRoute = lazy(() => import("./OfferTakeoverRoute"));

export enum MaintenanceFlagVariants {
  "control" = "control",
  "disable-everything" = "disable-everything",
  "flights" = "flights",
  "hotels" = "hotels",
  "trips" = "trips",
  "exclude-trips" = "exclude-trips",
}

const slots: Slots = {
  "text-input-field": InputTextField,
  "flight-shop-loading-screen": LoadingScreen,
  "trips-list-loading-screen": Loader,
  "mobile-flight-price-prediction-content": MobileFlightPricePredictionContent,
  "flight-shop-list-mobile-card": NubankFlightCard,
  "flight-shop-mobile-header": NubankFlightShopMobileHeader,
  "flight-shop-list-expanded-section": FlightCardExpandedSection,
  "fare-details-slice-header": FareDetailsSliceHeader,
  "flight-shop-cta-section": SharedFlightCtaSection,
  "mobile-flight-number-filter-selection": FlightNumberFilterSelection,
  "mobile-flight-filter-title": FilterTitle,
  "add-traveler-form-select": AddTravelerFormSelect,
  "checkout-contact-info-form": ContactInfoForm,
  "passenger-frequent-flyer-select": FrequentFlyerDropdown,
  "passenger-required-assistance-select": RequiredAssistanceSelect,
  "passenger-passport-country-select": PassportCountrySelect,
  "passenger-input-field-learn-more": InputAdornmentLearnMore,
  "flight-review-mixed-cabin-tooltip": MixedCabinHalfsheet,
  "flight-shop-summary-panel": FlightShopSummaryPanel,
  "trip-details-links": MobileActionLinks,
  "trip-details-summary": TripDetailsSummary,
  "empty-trips-list": NoResults,
  "flight-price-drop-protection-banner": FlightPriceDropProtectionBanner,
  "flight-shop-location-search-destination-autocomplete":
    FlightShopLocationDestinationAutocomplete,
  "flight-shop-location-search-origin-autocomplete":
    FlightShopLocationOriginAutocomplete,
  "flight-search-mobile-header-left-content":
    FlightSearchMobileHeaderLeftContent,
  "price-breakdown-additional-info": PriceBreakdownAdditionalInfo,
  "flight-shop-review-multiticket-banner": FlightShopReviewMultiticketBanner,
  "flight-shop-review-insurance-section": FlightShopReviewInsuranceSection,
  "dropdown-icon": ChevronDown,
  "radio-dropdown": RadioDropdown,
  "traveler-select-row-button-wrap": TravelerSelectRowButtonWrap,
  // Use EmptySlot to match Uber - the dynamic show/hide was causing layout shifts
  "passenger-form-mobile-top-save-right-button": EmptySlot,
  // Renders save/delete in the modal header instead of at the bottom of the form
  "mobile-traveler-edit-form-buttons": HeaderFormButtons,
  "passenger-select-button": PassengerSelectButton,
  // Use simple modal instead of MUI Dialog to fix iOS Safari iframe issues
  "traveller-form-modal": TravellerFormModal,
  "flight-confirmation-more-information-container":
    FlightConfirmationTravelInsuranceIncluded,
  "price-watch-entry-card": PriceWatchEntryCard as any,

  // Self-serve
  "self-serve-cancellation-flight-info": SelfServeFlightInfo,
  "self-serve-cancellation-error-screen": ErrorScreen,
  "exchange-loader": Loader,
  "exchange-error-screen": ErrorScreen,
  "exchange-contact-airline-screen": ExchangeContactAirlineScreen,
  "exchange-landing-screen": ExchangeLandingScreen,
  "exchange-request-confirmation-screen": ConfirmationScreen,
  "trips-confirmation-modal-copy-and-go-button":
    TripsConfirmationCopyAndGoButton,
  "trips-error-modal-content": TripsErrorModalContent,
  "hotel-cancellation-confirmation-screen": HotelCancellationConfirmationScreen,

  // Hotels
  "hotel-shop-filters-modal": FiltersModal,
  "lodging-details-screen-header": HotelHeader,
  "hotel-shop-header": ShopHeader,
  "hotel-shop-search-modal": SearchModal,
  "lodging-availability-search": LodgingSearch,
  "hotel-details-gallery-header": GalleryHeader,
  "hotel-details-gallery-grid": GalleryGrid,
  "hotel-details-policies": HotelDetailsPolicies,
  "hotel-details-review": HotelDetailsReview,
  "hotel-shop-map": HotelShopMap,
  "hotel-room-details-header": RoomDetailsHeader,
  "hotel-room-details-select": RoomDetailsSelect,
  "hotel-room-details-modal-header": RoomDetailsModalHeader,
  "hotel-room-details-gallery": RoomGallery,
  "hotel-room-details-cta": RoomDetailsCta,
  "lodging-search-mobile-guest-modal": LodgingSearchMobileGuestModal,
  "lodging-card-cash-back": LodgingCardCashBack,
  "hotel-summary-price": LodgingCardPrice,
  "lodging-card-original-price": LodgingCardOriginalPrice,
  "hotel-list-map-card": HotelListMapCard,
  "hotel-voucher-offer-banner": HotelVoucherOfferBanner,
  "flight-search-banner": BlackFridayBanner,
  "offer-banner": OfferBanner,
  "hotel-cancellation-summary-panel": HotelCancellationSummaryPanel,
  "hotel-cancellation-loader": LoadingScreen,
  "hotel-search-autocomplete": HotelSearchAutocomplete,
  "best-price-guarantee-banner": BestPriceGuaranteeBanner,
  "installments-banner": InstallmentsBanner,
  "lodging-map-back-button": LodgingMapBackButton,

  // Trips
  "mytrips-list": UberTripsList,
  "mytrips-trip-card": TripsCard,
  "contact-support-modal-content": ContactSupportModalContent,
  "mytrips-modal-close-icon": TripsModalCloseIcon,
  "trip-flights-details-insurance": TripFlightsDetailsInsurance,
  "mytrips-flights-details-baggage": TripsFlightsDetailsBaggage,
  "mytrips-flights-details-seats": TripsFlightsDetailsSeats,
  "mytrips-flights-payment-details-additional-info":
    TripsFlightsPaymentDetailsAdditionalInfo,
  "mytrips-flights-convenience-fee": TripsFlightsConvenienceFee,
};

interface IBodyProps {
  theme: Theme;
  className?: string;
  isFirstSession: boolean;
  launchEventSent: boolean;
  setLaunchEventSent: (val: boolean) => void;
  onDarkModeChange: (val: boolean) => void;
}

const Body = (props: IBodyProps) => {
  const {
    theme,
    className,
    launchEventSent,
    setLaunchEventSent,
    onDarkModeChange,
  } = props;
  const classes = useStyles();

  const { sessionInfo } = useContext(UserContext);

  const { generalSupportChat } = useChatSupport();

  const { pathname, search } = useLocation();

  const [isNupayOAuthRedirecting, setIsNupayOAuthRedirecting] = useState(false);

  // NuPay OAuth callback redirect: NuPay only allows a root redirect_uri
  // (e.g., https://nuviagens.com/), so the callback lands at "/" with
  // ?state=<base64>&code=<auth_code>. Use a hard navigation so Istio can
  // serve commerce /stays/ and /air/ checkout (client-side <Navigate> stays
  // in the b2bportal SPA and hits the /* catch-all back to home).
  useEffect(() => {
    const redirectUrl = resolveNupayOAuthCallbackRedirectUrl({
      pathname,
      search,
    });
    if (!redirectUrl) {
      return;
    }

    const oauth = parseNupayOAuthCallbackSearch(search);
    logger()?.info("NuPay OAuth callback: redirecting to checkout", {
      ...(oauth
        ? getNupayOAuthRedirectLogMetadata(redirectUrl, oauth)
        : { returnPath: redirectUrl.split("?")[0] }),
    });
    setIsNupayOAuthRedirecting(true);
    window.location.replace(redirectUrl);
  }, [pathname, search]);

  const { isOpened, isChatCollapsed } = useKustomerChatSupport();

  const [showMaintenance, setShowMaintenance] = useState<boolean>(false);

  // Get feature flags from context (with debug menu overrides)
  const ffContext = useNubankFeatureFlagsContext();

  const appMaintenance = ffContext.appMaintenance;
  const appExcludingTripsMaintenance = ffContext.appExcludingTripsMaintenance;
  const flightsMaintenance = ffContext.flightsMaintenance;
  const hotelsMaintenance = ffContext.hotelsMaintenance;
  const tripsMaintenance = ffContext.tripsMaintenance;

  // Fintech Flags
  const enablePriceWatch = ffContext.enablePriceWatch;
  const enableDisruptionExerciseRebook =
    ffContext.enableDisruptionExerciseRebook;

  const enableCfar = ffContext.enableCfar;
  const enableChfar = ffContext.enableChfar;
  const enableVipSupport = ffContext.enableVipSupport;
  const enableDisruptionExerciseRebooking =
    ffContext.enableDisruptionExerciseRebooking;
  const enablePricePrediction = ffContext.enablePricePrediction;
  const enablePriceDropProtection = ffContext.enablePriceDropProtection;

  // Features
  const enableAirExchange = ffContext.enableAirExchange;
  const enableDarkMode = ffContext.enableDarkMode;
  const enableAirShopV4 = ffContext.enableAirShopV4; //Defaults handled in context merge
  const enableLoadingSteps = ffContext.enableLoadingSteps;
  const enableLodging = ffContext.enableLodging; //Defaults handled in context merge
  const enableHFv2 = ffContext.enableHFv2;
  const enableWalletOffers = ffContext.enableWalletOffers;
  const enableWalletCredits = ffContext.enableWalletCredits;
  const enableLodgingMap = ffContext.enableLodgingMap;
  const enableLodgingExpressCheckout = ffContext.enableLodgingExpressCheckout;
  const enableExtraBags = ffContext.enableExtraBags;
  const enableEnhancedEarn = ffContext.enableEnhancedEarn;
  const enableNupayDirect = ffContext.enableNupayDirect;
  // Experiments (from context)
  const hideAirItineraryReview = ffContext.hideAirItineraryReview;
  const hideFareDetails = ffContext.hideFareDetails;
  const enableFintechSelection = ffContext.enableFintechSelection;
  const seatsAvailable = ffContext.seatsAvailable;
  const enableVouchers10 = ffContext.enableVouchers10;
  const enableVouchers20 = ffContext.enableVouchers20;
  const replaceMaterialUI = ffContext.replaceMaterialUI;
  const renderLodgingStrikethroughPrice =
    ffContext.renderLodgingStrikethroughPrice;
  const renderHotelMapEntryPoint = ffContext.renderHotelMapEntryPoint;
  const nubankMaxInstallments = ffContext.nubankMaxInstallments;
  const enableBlackFridayExperiment = ffContext.enableBlackFridayExperiment;
  const enableInstallmentsCampaign =
    ffContext.enableInstallmentsCampaign as string;
  const renderLodgingShopValueProps =
    ffContext.renderLodgingShopValueProps as RenderLodgingShopValuePropsVariant;
  const renderHotelMapAsDefaultView = ffContext.renderHotelMapAsDefaultView;
  const renderExtraHotelAmenities = ffContext.renderExtraHotelAmenities as any;
  const enableTravelInsurance = ffContext.enableTravelInsurance;
  const enableHotelDetailsCtaCopy = ffContext.enableHotelDetailsCtaCopy;
  const roomDetailsCtaCopy = ffContext.roomDetailsCtaCopy;
  const inputFocusHandling = ffContext.inputFocusHandling;

  const trackingProperties: LaunchedApplicationProperties =
    useTrackingProperties({
      landing_screen: getLandingScreen(),
      url: document.location.pathname,
    });

  const launchSent = useLaunchedApplicationEvent(
    launchEventSent,
    trackingProperties,
    trackEvent
  );

  useEffect(() => {
    if (launchSent) {
      setLaunchEventSent(true);
    }
  }, [launchSent, setLaunchEventSent]);

  const isDarkMode = useDarkModePreferred();

  const featureFlags: FeatureFlagsContextProps = useMemo(
    () => ({
      enableAirExchange,
      enableCfar,
      enableChfar,
      enableDarkMode: enableDarkMode && isDarkMode,
      enableFintechSelection,
      enablePriceWatch,
      enableVipSupport,
      enableDisruptionExerciseRebooking,
      hideAirItineraryReview,
      hideFareDetails,
      hideSeats: seatsAvailable !== true,
      enableAirShopV4,
      enableLoadingSteps,
      enablePricePrediction,
      enableLodging,
      enablePriceDropProtection,
      enableHFv2,
      enableWallet: enableWalletOffers, // use offers to control generic wallet features
      enableWalletOffers, // offer specific features
      enableWalletCredits, // credit specific features
      hideLodgingMap: enableLodgingMap !== true,
      enableVouchers10,
      enableVouchers20,
      replaceMaterialUI,
      renderLodgingStrikethroughPrice,
      renderHotelMapEntryPoint,
      enableBlackFridayExperiment,
      nubankMaxInstallments,
      renderLodgingShopValueProps,
      renderExtraHotelAmenities,
      enableLodgingExpressCheckout,
      enableTravelInsurance,
      enableDisruptionExerciseRebook,
      renderHotelMapAsDefaultView,
      enableHotelDetailsCtaCopy,
      roomDetailsCtaCopy,
      useNativeTextInputs: true,
      inputFocusHandling,
      enableInstallmentsCampaign,
      enableExtraBags,
      enableCfarExerciseCore: true,
      enableEnhancedEarn,
      enableNupayDirect,
    }),
    [
      enableAirExchange,
      enableCfar,
      enableChfar,
      enableDarkMode,
      isDarkMode,
      enableFintechSelection,
      enablePriceWatch,
      enableVipSupport,
      enableDisruptionExerciseRebooking,
      hideAirItineraryReview,
      hideFareDetails,
      seatsAvailable,
      enableAirShopV4,
      enableLoadingSteps,
      enablePricePrediction,
      enableLodging,
      enablePriceDropProtection,
      enableHFv2,
      enableWalletOffers,
      enableWalletCredits,
      enableLodgingMap,
      enableVouchers10,
      enableVouchers20,
      replaceMaterialUI,
      renderLodgingStrikethroughPrice,
      renderHotelMapEntryPoint,
      enableBlackFridayExperiment,
      nubankMaxInstallments,
      renderLodgingShopValueProps,
      renderExtraHotelAmenities,
      enableLodgingExpressCheckout,
      enableTravelInsurance,
      enableDisruptionExerciseRebook,
      renderHotelMapAsDefaultView,
      enableHotelDetailsCtaCopy,
      roomDetailsCtaCopy,
      inputFocusHandling,
      enableInstallmentsCampaign,
      enableExtraBags,
      enableEnhancedEarn,
      enableNupayDirect,
    ]
  );

  useEffect(() => {
    if (enableDarkMode && isDarkMode) {
      onDarkModeChange(true);
    }
  }, [enableDarkMode, isDarkMode, onDarkModeChange]);

  // Adding DD RUM tracking for non-prod environments ONLY

  useEffect(() => {
    if (config.ENV !== "production") {
      const promise = document?.hasStorageAccess?.();
      promise?.then(
        function (hasAccess) {
          logger().info(`hasStorageAccess - success: ${hasAccess}`);
        },
        function (reason) {
          logger().info(`hasStorageAccess - failed: ${reason}`);
        }
      );
      if (!promise) {
        logger().info("hasStorageAccess does not exist on document");
      }
    }
  }, []);

  useEffect(() => {
    setShowMaintenance(false);

    if (appMaintenance) {
      setShowMaintenance(true);
      return;
    }

    if (appExcludingTripsMaintenance && !pathname.includes(PATH_TRIPS)) {
      setShowMaintenance(true);
      return;
    }

    const isUsingFlights = pathname.includes(PATH_FLIGHTS);
    const isUsingHotels = pathname.includes(PATH_HOTELS_ROOT);
    const isUsingTrips = pathname.includes(PATH_TRIPS);

    // Check maintenance flags
    if (
      (flightsMaintenance && isUsingFlights) ||
      (hotelsMaintenance && isUsingHotels) ||
      (tripsMaintenance && isUsingTrips)
    ) {
      setShowMaintenance(true);
      return;
    }
  }, [
    pathname,
    appMaintenance,
    appExcludingTripsMaintenance,
    flightsMaintenance,
    hotelsMaintenance,
    tripsMaintenance,
  ]);

  const portalAccess = sessionInfo?.userInfoResponse?.portalAccess;
  const hasSupportAccess = portalAccess?.support !== false;

  useEffect(() => {
    const urlParams = new URLSearchParams(search);
    const hasChatSupport = urlParams.has("chat-support");

    // If we're in chat support mode AND user has collapsed/closed the chat
    // AND we're on the home screen, close the webview to return to Nubank app
    if (hasChatSupport && isChatCollapsed && pathname === PATH_HOME) {
      closeNubankWebview();
    }
  }, [isChatCollapsed, pathname, search]);

  const showLoading = useMemo(() => {
    const urlParams = new URLSearchParams(search);
    const hasChatSupport = urlParams.has("chat-support");

    // Don't show loading if support access is restricted (will show AccessRestricted instead)
    if (hasChatSupport && !hasSupportAccess) {
      return false;
    }

    // Show loading spinner when we're in chat support mode (opened from Nubank app)
    return hasChatSupport && !isOpened && !isChatCollapsed;
  }, [isOpened, isChatCollapsed, search, hasSupportAccess]);

  // NuPay OAuth callback: full-page redirect to commerce or legacy checkout
  if (isNupayOAuthRedirecting) {
    return (
      <div className="main-loading">
        <Spinner />
      </div>
    );
  }

  // Redirect to access restricted if trying to access chat support without permission
  const urlParams = new URLSearchParams(search);
  if (urlParams.has("chat-support") && !hasSupportAccess) {
    return <Navigate to={PATH_ACCESS_RESTRICTED} replace />;
  }

  if (showLoading) {
    return (
      <div className="main-loading">
        <Spinner />
      </div>
    );
  }
  const hasFlightsAccess = portalAccess?.flights !== false;
  const hasHotelsAccess = portalAccess?.hotels !== false;
  const hasTripsAccess = portalAccess?.myTrips !== false;

  return (
    <main
      className={clsx(
        "main-body",
        classes.main,
        className,
        getEnvVariables("clientName")
      )}
    >
      {showMaintenance ? (
        <Maintenance />
      ) : (
        <Grid className={classes.content}>
          <Routes>
            <Route
              path={PATH_ACCESS_RESTRICTED}
              element={<AccessRestricted />}
            />

            {/* TODO: Add landing page */}
            <Route
              path={"/*"}
              element={<Navigate to={PATH_LANDING} replace={true} />}
            />
            <Route
              path={PATH_HOME}
              element={<HomeRouteComponent featureFlags={featureFlags} />}
            />
            <Route
              path={PATH_OFFERS}
              element={
                <Suspense fallback={<OfferTakeoverSkeleton />}>
                  <OfferTakeoverRoute />
                </Suspense>
              }
            />
            <Route
              path={PATH_FLIGHTS_SEARCH}
              element={
                <FeatureProtectedRoute
                  hasAccess={hasFlightsAccess}
                  redirectTo={PATH_ACCESS_RESTRICTED}
                >
                  <Suspense>
                    <FlightSearchRoute
                      featureFlags={featureFlags}
                      theme={theme}
                      slots={{
                        ...slots,
                        "mobile-checkout-cta-section": FlightCheckoutCtaSection,
                      }}
                    />
                  </Suspense>
                </FeatureProtectedRoute>
              }
            />
            <Route
              path={PATH_FLIGHTS_SHOP}
              element={
                <FeatureProtectedRoute
                  hasAccess={hasFlightsAccess}
                  redirectTo={PATH_ACCESS_RESTRICTED}
                >
                  <Suspense>
                    <FlightSearchRoute
                      entryPoint="shop"
                      featureFlags={featureFlags}
                      theme={theme}
                      slots={{
                        ...slots,
                        "mobile-checkout-cta-section": FlightCheckoutCtaSection,
                      }}
                    />
                  </Suspense>
                </FeatureProtectedRoute>
              }
            />
            <Route
              path={ROUTE_FLIGHTS}
              element={
                <FeatureProtectedRoute
                  hasAccess={hasFlightsAccess}
                  redirectTo={PATH_ACCESS_RESTRICTED}
                >
                  <Suspense>
                    <FlightsRouteComponent
                      featureFlags={featureFlags}
                      theme={theme}
                      slots={{
                        ...slots,
                        "mobile-checkout-cta-section": FlightCheckoutCtaSection,
                      }}
                    />
                  </Suspense>
                </FeatureProtectedRoute>
              }
            />
            <Route
              path={PATH_HOTELS_SEARCH}
              element={
                <FeatureProtectedRoute
                  hasAccess={hasHotelsAccess}
                  redirectTo={PATH_ACCESS_RESTRICTED}
                >
                  <Suspense>
                    <LodgingSearchRoute />
                  </Suspense>
                </FeatureProtectedRoute>
              }
            />
            <Route
              path={ROUTE_HOTELS}
              element={
                <FeatureProtectedRoute
                  hasAccess={hasHotelsAccess}
                  redirectTo={PATH_ACCESS_RESTRICTED}
                >
                  <Suspense>
                    {/* TODO: nubank loading screen */}
                    <LodgingRouteComponent
                      //Do not use client assets, use TenantContext for icons and images
                      featureFlags={{
                        ...featureFlags,
                        showHorizontalScrollListInMobileMap: true,
                      }}
                      slots={{
                        ...slots,
                        "mobile-checkout-cta-section": HotelCheckoutCtaSection,
                      }}
                    />
                  </Suspense>
                </FeatureProtectedRoute>
              }
            />
            <Route
              path={`${PATH_TRIPS}*`}
              element={
                <FeatureProtectedRoute
                  hasAccess={hasTripsAccess}
                  redirectTo={PATH_ACCESS_RESTRICTED}
                >
                  <Suspense>
                    <TripRoute
                      featureFlags={featureFlags}
                      theme={theme}
                      slots={slots}
                    />
                  </Suspense>
                </FeatureProtectedRoute>
              }
            />
            {/* Below path is used for deep links from email?  */}
            <Route
              path={`${BASE_PATH_DISRUPTION}*`}
              element={
                <Suspense>
                  <DisruptionExerciseRoute
                    openSupportChat={generalSupportChat}
                    slots={slots}
                  />
                </Suspense>
              }
            />
            <Route
              path={`trips/disruption/exercise/:reservationId`}
              element={
                <Suspense>
                  <DisruptionExerciseRoute
                    openSupportChat={generalSupportChat}
                    slots={slots}
                  />
                </Suspense>
              }
            />
            <Route
              key={"air-price-watch"}
              path={`watches/*`}
              element={
                <FeatureProtectedRoute
                  hasAccess={hasFlightsAccess}
                  redirectTo={PATH_ACCESS_RESTRICTED}
                >
                  <Suspense>
                    <WatchesApp />
                  </Suspense>
                </FeatureProtectedRoute>
              }
            />
          </Routes>
        </Grid>
      )}
    </main>
  );
};

export default Body;
